import { CSSProperties, useMemo } from 'react'
import { LoggedOutGeneratorSection } from '../../LoggedOutCodeGenerator.types'
import { SystemStyleObject } from '@mui/system'
import { TRANSITION_DURATION } from '../../LoggedOutCodeGenerator.constants'
import { Theme } from '@mui/material'
import { selectSectionGroup } from '@app/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { usePreviousValue } from '@app/page/src/components/util/usePreviousValue'
import { useSelector } from '@xstate/react'

const commonTransitionStyles: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0
}
const transitionPropertyStyle: CSSProperties = {
  transition: `opacity ${TRANSITION_DURATION}ms, transform 500ms ease-in-out`
}
const transitionEnterActiveAndExitStyles: CSSProperties = {
  opacity: 1,
  transform: 'none'
}

interface BuildTransitionStyleArgs {
  sectionName: LoggedOutGeneratorSection
  containerHeight: string
}

// when scrolling up
const buildEnterFromTopStyles = ({
  sectionName,
  containerHeight
}: BuildTransitionStyleArgs): SystemStyleObject<Theme> => ({
  [`.${sectionName}-enter`]: {
    ...commonTransitionStyles,
    transform: `translateY(-${containerHeight})`,
    opacity: 0
  },
  [`.${sectionName}-enter-active`]: {
    ...commonTransitionStyles,
    ...transitionEnterActiveAndExitStyles,
    ...transitionPropertyStyle
  },
  [`.${sectionName}-exit`]: {
    ...commonTransitionStyles,
    ...transitionEnterActiveAndExitStyles
  },
  [`.${sectionName}-exit-active`]: {
    ...commonTransitionStyles,
    ...transitionPropertyStyle,
    opacity: 0,
    transform: `translateY(-${containerHeight})`
  }
})

// when scrolling down
const buildEnterFromBottomStyles = ({
  sectionName,
  containerHeight
}: BuildTransitionStyleArgs): SystemStyleObject<Theme> => ({
  [`.${sectionName}-enter`]: {
    ...commonTransitionStyles,
    opacity: 0,
    transform: `translateY(${containerHeight})`
  },
  [`.${sectionName}-enter-active`]: {
    ...commonTransitionStyles,
    ...transitionEnterActiveAndExitStyles,
    ...transitionPropertyStyle
  },
  [`.${sectionName}-exit`]: {
    ...commonTransitionStyles,
    ...transitionEnterActiveAndExitStyles
  },
  [`.${sectionName}-exit-active`]: {
    ...commonTransitionStyles,
    ...transitionPropertyStyle,
    opacity: 0,
    transform: `translateY(${containerHeight})`
  }
})

export interface LOCGSectionTransitionStyles {
  scanDestinationSectionStyles: SystemStyleObject<Theme>
  codeDesignSectionStyles: SystemStyleObject<Theme>
  authSectionStyles: SystemStyleObject<Theme>
}

export const useLOCGSectionTransitionStyles = (
  containerHeight: string
): LOCGSectionTransitionStyles => {
  const { locgService } = useGlobalServices()
  const currentSection = useSelector(locgService, selectSectionGroup)
  const previousSection = usePreviousValue(currentSection)

  const scanDestinationSectionStyles = useMemo(() => {
    return buildEnterFromTopStyles({
      sectionName: LoggedOutGeneratorSection.ScanDestination,
      containerHeight
    })
  }, [containerHeight])

  const codeDesignSectionStyles = useMemo(() => {
    if (currentSection === LoggedOutGeneratorSection.ScanDestination) {
      return buildEnterFromBottomStyles({
        sectionName: LoggedOutGeneratorSection.CodeDesign,
        containerHeight
      })
    }
    if (currentSection === LoggedOutGeneratorSection.SignUp) {
      return buildEnterFromTopStyles({
        sectionName: LoggedOutGeneratorSection.CodeDesign,
        containerHeight
      })
    }
    if (currentSection === LoggedOutGeneratorSection.CodeDesign) {
      if (previousSection === LoggedOutGeneratorSection.ScanDestination) {
        return buildEnterFromBottomStyles({
          sectionName: LoggedOutGeneratorSection.CodeDesign,
          containerHeight
        })
      }
      if (previousSection === LoggedOutGeneratorSection.SignUp) {
        return buildEnterFromTopStyles({
          sectionName: LoggedOutGeneratorSection.CodeDesign,
          containerHeight
        })
      }
    }
    return buildEnterFromBottomStyles({
      sectionName: LoggedOutGeneratorSection.CodeDesign,
      containerHeight
    })
  }, [containerHeight, currentSection, previousSection])

  const authSectionStyles = useMemo(() => {
    return buildEnterFromBottomStyles({
      sectionName: LoggedOutGeneratorSection.SignUp,
      containerHeight
    })
  }, [containerHeight])

  return {
    scanDestinationSectionStyles,
    codeDesignSectionStyles,
    authSectionStyles
  }
}
